import React, { useState } from 'react';
import { Copy, Check, Calendar, Loader2, Save, FileText, Eye, X, Download, Wand2, Circle, CircleDot } from 'lucide-react';
import type { VnFormData } from '../lib/vorgangApi';
import { fetchFileBlob } from '../lib/vorgangApi';
import { vnAnzeigeDokumente } from '../lib/vnDokumente';
import { computeElster } from '../lib/elster';
import { api } from '../lib/api';
import { ESF_SECTIONS } from '../lib/esfFragebogen';

interface Props {
  data: VnFormData | null;
  loading: boolean;
  customerId?: number;
  initialNotes?: string;
  onFillPage1?: () => void;
  /** Füllt Seite 3 (ESF-Grundsätze-Fragebogen) aus den gespeicherten esfSelections. */
  onFillEsfGrundsaetze?: () => void;
  /** Welche BAFA-Unterseite der iframe links gerade zeigt — steuert den Hinweis-Banner.
   *  'esf-monitoring': Seite 2 (Zufriedenheitsfragen) hat keine Entsprechung in unseren
   *  Daten. 'esf-grundsaetze': Seite 3, aus esfSelections befüllbar (onFillEsfGrundsaetze). */
  bafaPage?: 'seite1' | 'esf-monitoring' | 'esf-grundsaetze' | 'andere';
}

/** Editierbares Kunden-Notizfeld (speichert notes am Kunden). */
function NotizField({ customerId, initial }: { customerId?: number; initial?: string }) {
  const [value, setValue] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = value !== (initial || '');
  const save = async () => {
    if (!customerId) return;
    setSaving(true);
    try {
      await api.patch(`/api/customers/${customerId}`, { notes: value });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      alert('Notiz konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="py-2">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        placeholder="Notiz zum Kunden…"
        className="w-full text-sm rounded-lg border border-slate-300 px-2 py-1.5 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex justify-end mt-1">
        <button
          onClick={save}
          disabled={!customerId || saving || !dirty}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
          {saved ? 'Gespeichert' : 'Notiz speichern'}
        </button>
      </div>
    </div>
  );
}

function CopyField({ label, value }: { label: string; value: string | null | undefined }) {
  const [copied, setCopied] = useState(false);
  const display = value || '—';
  const canCopy = !!value;
  const handleCopy = () => {
    if (!canCopy) return;
    navigator.clipboard.writeText(value!);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-start py-2 border-b border-slate-100 last:border-0 gap-2">
      {canCopy ? (
        <button onClick={handleCopy} aria-label="Kopieren"
          className="shrink-0 mt-0.5 p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors">
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      ) : <div className="w-7 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className="text-sm text-slate-900 break-words whitespace-pre-wrap">{display}</p>
      </div>
    </div>
  );
}

function DateField({ label, value }: { label: string; value: string | null | undefined }) {
  // YYYY-MM-DD → DD.MM.JJJJ (BAFA-Format)
  const fmt = (v?: string | null) => {
    if (!v) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
  };
  return <CopyFieldWithIcon icon={<Calendar className="w-3.5 h-3.5" />} label={label} value={fmt(value)} />;
}

function CopyFieldWithIcon({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null | undefined }) {
  const [copied, setCopied] = useState(false);
  const display = value || '—';
  const canCopy = !!value;
  const handleCopy = () => { if (canCopy) { navigator.clipboard.writeText(value!); setCopied(true); setTimeout(() => setCopied(false), 2000); } };
  return (
    <div className="flex items-start py-2 border-b border-slate-100 last:border-0 gap-2">
      <button onClick={handleCopy} disabled={!canCopy} aria-label="Kopieren"
        className="shrink-0 mt-0.5 p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50">
        {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : (canCopy ? <Copy className="w-3.5 h-3.5" /> : icon)}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className="text-sm text-slate-900 break-words">{display}</p>
      </div>
    </div>
  );
}

/** Dezenter Platzhalter-Hinweis im CopyField-Stil, z. B. für Werte, die im Bericht fehlen. */
function InfoField({ label, hint }: { label: string; hint: string }) {
  return (
    <div className="flex items-start py-2 border-b border-slate-100 last:border-0 gap-2">
      <div className="w-7 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className="text-sm text-slate-400 italic">{hint}</p>
      </div>
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-5 mb-2 pb-1 border-b border-slate-200 first:mt-0">
      {title}
    </h3>
  );
}

function TipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 mb-1 px-2 py-2 bg-green-50 rounded-lg border border-green-200">
      <p className="text-xs text-green-800">💡 {children}</p>
    </div>
  );
}

function fields(rec: Record<string, string | null | undefined>) {
  return Object.entries(rec).map(([label, value]) => <CopyField key={label} label={label} value={value} />);
}

export function VnDatenPanel({ data, loading, customerId, initialNotes, onFillPage1, onFillEsfGrundsaetze, bafaPage }: Props) {
  const [preview, setPreview] = useState<{ name: string; url: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState<number | null>(null);
  const [downloadLoading, setDownloadLoading] = useState<number | null>(null);
  const openPreview = async (doc: { id: number; label: string }) => {
    setPreviewLoading(doc.id);
    try {
      const { url } = await fetchFileBlob(doc.id);
      setPreview({ name: doc.label, url });
    } catch {
      alert('Vorschau konnte nicht geladen werden.');
    } finally {
      setPreviewLoading(null);
    }
  };
  /** Dokument auf die Festplatte speichern — unter dem Namen aus der Dokumentenablage. */
  const downloadDoc = async (doc: { id: number; dateiname: string }) => {
    setDownloadLoading(doc.id);
    try {
      const { url } = await fetchFileBlob(doc.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.dateiname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      alert('Download fehlgeschlagen.');
    } finally {
      setDownloadLoading(null);
    }
  };
  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  if (loading) {
    return (
      <div className="p-4 h-full flex items-center justify-center text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Daten werden geladen…
      </div>
    );
  }
  if (!data) {
    return <div className="p-4 text-sm text-slate-500">Keine Daten verfügbar.</div>;
  }

  // Nur die für den VN gebrauchten Dokumentarten, umbenannt und fest sortiert (siehe vnDokumente.ts).
  const anzeigeDokumente = vnAnzeigeDokumente(data.dokumente);

  return (
    <>
    <div className="p-4 overflow-y-auto h-full">
      {onFillPage1 && (
        <button
          onClick={onFillPage1}
          className="w-full mb-3 flex items-center justify-center gap-1.5 text-sm font-medium text-violet-600 border border-violet-200 rounded-lg px-3 py-2 hover:bg-violet-50 transition-colors"
        >
          <Wand2 className="w-4 h-4" />
          Formular ausfüllen
        </button>
      )}
      {bafaPage === 'esf-monitoring' && (
        <div className="mb-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
          Seite 2: Monitoring-/Zufriedenheitsfragen der BAFA — rein subjektiv, dafür liegen keine Daten im System vor. Bitte direkt im Formular beantworten.
        </div>
      )}
      {bafaPage === 'esf-grundsaetze' && (
        onFillEsfGrundsaetze ? (
          <button
            onClick={onFillEsfGrundsaetze}
            className="w-full mb-3 flex items-center justify-center gap-1.5 text-sm font-medium text-violet-600 border border-violet-200 rounded-lg px-3 py-2 hover:bg-violet-50 transition-colors"
          >
            <Wand2 className="w-4 h-4" />
            ESF-Grundsätze aus gespeicherten Antworten ausfüllen
          </button>
        ) : (
          <div className="mb-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            Seite 3: ESF-Grundsätze-Fragebogen — für diesen Kunden liegen keine gespeicherten Antworten vor (siehe Abschnitt unten). Bitte direkt im Formular beantworten.
          </div>
        )
      )}
      {bafaPage === 'andere' && (
        <div className="mb-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
          Andere Formularseite erkannt — die Felder unten gehören zu Seite 1 des Formulars.
        </div>
      )}
      {/* 0. Kontext: Vorgang, Bericht, Dokumente, verknüpfte Vorgänge, Notiz */}
      {data.vorgangsnummer && (
        <>
          <SectionTitle title="Vorgang" />
          <CopyField label="Vorgangsnummer" value={data.vorgangsnummer} />
        </>
      )}
      {data.berichtTitel && (
        <>
          <SectionTitle title="Bericht" />
          <CopyField label="Thema des Berichts" value={data.berichtTitel} />
        </>
      )}
      {anzeigeDokumente.length > 0 && (
        <>
          <SectionTitle title="Dokumente (Vorschau)" />
          <div className="py-1 space-y-1">
            {anzeigeDokumente.map((doc) => (
              <div key={doc.id} className="flex items-center gap-1 rounded-md hover:bg-slate-200 transition-colors">
                <button
                  onClick={() => openPreview(doc)}
                  disabled={previewLoading === doc.id}
                  title={`${doc.label} — Vorschau`}
                  className="flex-1 min-w-0 flex items-center gap-2 text-left px-2 py-1.5 text-sm text-slate-700 disabled:opacity-50"
                >
                  {previewLoading === doc.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0 text-slate-400" />
                    : <FileText className="w-3.5 h-3.5 shrink-0 text-slate-400" />}
                  <span className="truncate flex-1">{doc.label}</span>
                  <Eye className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                </button>
                <button
                  onClick={() => downloadDoc(doc)}
                  disabled={downloadLoading === doc.id}
                  title={`${doc.label} herunterladen`}
                  aria-label={`${doc.label} herunterladen`}
                  className="shrink-0 mr-1 p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-300 disabled:opacity-50 transition-colors"
                >
                  {downloadLoading === doc.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Download className="w-3.5 h-3.5" />}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      <SectionTitle title="Notiz zum Kunden" />
      <NotizField customerId={customerId} initial={initialNotes} />

      {/* 2. Durchgeführte Beratung */}
      <SectionTitle title="Durchgeführte Beratung" />
      <TipBox>
        Zuerst <strong>Beratungsbereich</strong> auswählen: wirtschaftlich · finanziell · personell · organisatorisch.
        {data.beratung.bereich && <> <br />KI-Vorschlag: <strong className="capitalize">{data.beratung.bereich}</strong></>}
      </TipBox>
      <DateField label="Beratungsbeginn" value={data.beratung.beginn} />
      <DateField label="Beratungsende" value={data.beratung.ende} />
      <CopyField label="Anzahl der Beratungstage" value={data.beratung.tage != null ? String(data.beratung.tage) : null} />
      <CopyField label="Inhalt und Ziel des Beratungsauftrages" value={data.beratung.inhaltZiel} />

      {/* 3. Kosten der Beratung */}
      <SectionTitle title="Kosten der Beratung" />
      {data.kosten.netto == null ? (
        <>
          <InfoField label="Kosten ohne USt." hint="Im Bericht nicht eingetragen" />
          <InfoField label="Kosten mit USt." hint="Im Bericht nicht eingetragen" />
        </>
      ) : (
        <>
          <CopyField label="Kosten ohne USt." value={data.kosten.netto} />
          <CopyField label="Kosten mit USt." value={data.kosten.mitUst} />
        </>
      )}

      {/* 4. Bankverbindung */}
      <SectionTitle title="Bankverbindung der antragstellenden Person" />
      <CopyField label="IBAN" value={data.bank.iban} />

      {/* 5. § 93a Abgabenordnung — Unternehmensdaten.
          Natürliche Person → Identifikationsnummer + Geburtsdatum (aus data.unternehmen), keine ELSTER.
          Juristische Person → nur Rechtsform + ELSTER-Steuernummer. */}
      <SectionTitle title="Allgemeine Mitteilungspflichten nach § 93a Abgabenordnung" />
      {fields(data.unternehmen)}
      {!data.natuerlichePerson && (
        <CopyField label="Steuernummer (ELSTER)" value={computeElster(data.steuernummer ?? '', data.plz ?? '', data.bundesland ?? undefined) || null} />
      )}

      {/* 6. De-minimis-Beihilfen */}
      <SectionTitle title="De-minimis-Beihilfen" />
      <TipBox>
        Alle De-minimis-Beihilfen des Unternehmens der <strong>letzten 3 Jahre</strong> angeben
        (Verordnung (EU) Nr. 2023/2831). Pro Beihilfe folgende Angaben eintragen:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li><strong>Datum des Bescheids</strong> (TT.MM.JJJJ)</li>
          <li><strong>Zuwendungsstelle</strong> (z. B. BAFA, KfW, Landesbank …)</li>
          <li><strong>Aktenzeichen</strong> des Bescheids</li>
          <li><strong>Art der Beihilfe</strong> (z. B. Zuschuss, Bürgschaft)</li>
          <li><strong>Betrag</strong> in €</li>
        </ul>
        <span className="block mt-1">Falls kein eigenständiges Unternehmen: auch Verbund-/Partnerunternehmen einbeziehen.
        Liegen keine vor, bleibt der Abschnitt leer.</span>
      </TipBox>
      {data.deminimis && data.deminimis.length > 0 ? (
        data.deminimis.map((d, i) => (
          <div key={i} className="mt-2">
            <p className="text-xs font-semibold text-slate-600 mb-0.5">{`De-minimis-Beihilfe Nr. ${i + 1}`}</p>
            <CopyField label="Datum des Bescheids" value={d.datum} />
            <CopyField label="Zuwendungsstelle" value={d.stelle} />
            <CopyField label="Aktenzeichen" value={d.aktenzeichen} />
            <CopyField label="Betrag" value={d.betrag} />
          </div>
        ))
      ) : (
        <p className="text-xs text-slate-400 italic py-2">Keine De-minimis-Beihilfen hinterlegt</p>
      )}

      {/* 7. ESF-Fragebogen (ausgefüllt) — als lesbare Antwortliste wie der Rest der Daten,
          keine PDF-Einbettung (die war unübersichtlich und zeigte das leere Musterformular,
          solange keine Antwort auf der Seite liegt). */}
      {data.esfSelections != null && (
        <>
          <SectionTitle title="ESF-Fragebogen (ausgefüllt)" />
          {ESF_SECTIONS.map((section) => (
            <div key={section.title} className="mt-2">
              <p className="text-xs font-semibold text-slate-600 mb-0.5">{section.title}</p>
              {section.questions.map((q) => (
                <div key={q.label} className="py-2 border-b border-slate-100 last:border-0">
                  <p className="text-xs text-slate-500 mb-1">{q.label}</p>
                  <div className="space-y-1">
                    {q.options.map((o) => {
                      const gewaehlt = data.esfSelections!.includes(o.name);
                      return (
                        <div key={o.name} className="flex items-start gap-2">
                          {gewaehlt
                            ? <CircleDot className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-600" />
                            : <Circle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-300" />}
                          <span className={`text-xs break-words ${gewaehlt ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
                            {o.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </>
      )}
    </div>

    {/* Vorschau-Modal (überlagert die ganze Seite) */}
    {preview && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={closePreview}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 shrink-0">
            <span className="text-sm font-medium text-slate-800 truncate">{preview.name}</span>
            <button onClick={closePreview} aria-label="Schließen" className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
              <X className="w-4 h-4" />
            </button>
          </div>
          <iframe src={preview.url} title={preview.name} className="flex-1 w-full border-0" />
        </div>
      </div>
    )}
    </>
  );
}
